import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, ExternalLink } from 'lucide-react';
import api from '../utils/api';

const Footer = () => {
  const [settings, setSettings] = useState(null);
  const [importantLinks, setImportantLinks] = useState([]);

  useEffect(() => {
    fetchSettings();
    fetchImportantLinks();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const fetchImportantLinks = async () => {
    try {
      const response = await api.get('/important-links');
      setImportantLinks(response.data || []);
    } catch (error) {
      console.error('Failed to fetch links:', error);
    }
  };

  return (
    <footer className="bg-card border-t border-white/10 py-12" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="Logo" className="h-10 w-auto" />
              <h3 className="text-xl font-bold text-primary">
                {settings?.store_name || 'ANANT WEB PORTAL'}
              </h3>
            </div>
            <p className="text-muted-foreground text-sm">
              Your one-stop destination for premium digital products, software, and courses.
            </p>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-foreground">Quick Links</h4>
            <div className="space-y-2">
              <Link to="/products" className="block text-muted-foreground hover:text-primary transition-colors">
                Products
              </Link>
              <Link to="/courses" className="block text-muted-foreground hover:text-primary transition-colors">
                Courses
              </Link>
              <Link to="/games" className="block text-muted-foreground hover:text-primary transition-colors">
                🎮 Games
              </Link>
              <Link to="/register" className="block text-muted-foreground hover:text-primary transition-colors">
                Join Us
              </Link>
              <Link to="/delivery-partner" data-testid="footer-delivery-partner-link"
                    className="block text-emerald-400 hover:text-emerald-300 transition-colors font-semibold">
                🛵 Delivery Partner Bano
              </Link>
            </div>
          </div>

          {/* Important Links (Admin Controlled) */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-foreground">Important Links</h4>
            <div className="space-y-2">
              {settings?.terms_url && (
                <a 
                  href={settings.terms_url} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="flex items-center gap-1 text-muted-foreground hover:text-primary transition-colors"
                >
                  Terms & Conditions <ExternalLink className="w-3 h-3" />
                </a>
              )}
              {settings?.privacy_url && (
                <a 
                  href={settings.privacy_url} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="flex items-center gap-1 text-muted-foreground hover:text-primary transition-colors"
                >
                  Privacy Policy <ExternalLink className="w-3 h-3" />
                </a>
              )}
              {settings?.refund_url && (
                <a 
                  href={settings.refund_url} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="flex items-center gap-1 text-muted-foreground hover:text-primary transition-colors"
                >
                  Refund Policy <ExternalLink className="w-3 h-3" />
                </a>
              )}
              {importantLinks.filter(l => l.is_active).map(link => (
                <a 
                  key={link.id}
                  href={link.url} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="flex items-center gap-1 text-muted-foreground hover:text-primary transition-colors"
                >
                  {link.title} <ExternalLink className="w-3 h-3" />
                </a>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-foreground">Contact Us</h4>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Phone className="w-4 h-4" />
                <span>{settings?.contact_number || '9579369830'}</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Mail className="w-4 h-4" />
                <span>{settings?.email || 'info@anantwebportal.com'}</span>
              </div>
            </div>
            
            {/* Payment Methods */}
            <div className="mt-4">
              <p className="text-sm text-muted-foreground mb-2">We Accept:</p>
              <div className="flex gap-2 items-center flex-wrap">
                <div className="px-3 py-1 bg-white/10 rounded text-xs font-semibold">UPI</div>
                {settings?.payment_gateways?.stripe && (
                  <div className="px-3 py-1 bg-white/10 rounded text-xs font-semibold">Stripe</div>
                )}
                {settings?.payment_gateways?.razorpay && (
                  <div className="px-3 py-1 bg-white/10 rounded text-xs font-semibold">Razorpay</div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-white/10 text-center text-muted-foreground text-sm">
          <p>&copy; {new Date().getFullYear()} {settings?.store_name || 'ANANT WEB PORTAL'}. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
